import { MouseEvent } from 'react';
import { usePagination } from 'react-instantsearch';
import PaginationNav from '_components/UI/PaginationNav';

interface PlpPaginationProps {
  /** Called after a plain left-click navigation is handled (e.g. scroll to top). */
  onNavigate?: () => void;
  className?: string;
}

// A modified click (new tab, middle-click, cmd/ctrl/shift/alt) should keep the
// browser's default behaviour so the real href stays useful. Only a plain
// left-click is intercepted for Algolia's client-side refinement.
const isModifiedEvent = (event: MouseEvent) =>
  event.button !== 0 ||
  event.altKey ||
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey;

/**
 * Connects the reusable `PaginationNav` UI to Algolia InstantSearch state.
 *
 * `createURL` builds the same `?page=N` hrefs the previous react-instantsearch
 * `<Pagination>` widget produced, so canonical URL handling and crawlable links
 * are unchanged. On a plain click we `refine` to drive the client-side query,
 * matching the widget's behaviour (GA4/search analytics fire exactly as before).
 */
const PlpPagination = ({ onNavigate, className }: PlpPaginationProps) => {
  const { currentRefinement, nbPages, createURL, refine } = usePagination();

  return (
    <PaginationNav
      className={className}
      // Uses PaginationNav's default sliding window (no ellipsis) — keeps the
      // current page centred and the control compact for PLPs.
      // InstantSearch pages are 0-indexed; PaginationNav is 1-indexed.
      currentPage={currentRefinement + 1}
      totalPages={nbPages}
      createHref={(page) => createURL(page - 1)}
      onNavigate={(page, event) => {
        if (isModifiedEvent(event)) return;
        event.preventDefault();
        refine(page - 1);
        onNavigate?.();
      }}
    />
  );
};

export default PlpPagination;
